import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Row,
  Table,
  Spin,
  message,
  Tag,
  Button,
  Skeleton,
  Col,
  Typography,
} from 'antd';
import { Download, LogIn, Clock, Activity, Zap, MousePointer, Coffee } from 'lucide-react';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import AdminDateFilters, { resolvePresetRange, filtersToParams } from './AdminDateFilters';
import WorkSessionsTimeline from './WorkSessionsTimeline';
import WorkRequestsTimeline from './WorkRequestsTimeline';
import WorkDistributionCharts from './WorkDistributionCharts';
import AdminKpiCard from './AdminKpiCard';
import AdminOnlineIndicator from './AdminOnlineIndicator';
import { exportUsersWorkSummary } from './exportWorkSummary';
import { PERIOD_CHART_TITLES } from '../../utils/chartPeriodFormat';
import { formatShortDateTime } from './adminFormatters';
import {
  getRequestGroupLabel,
  groupKeysFromMeta,
  aggregateByTypeObject,
} from '../../utils/requestTypeLabels';
import './adminLayout.sass';

const { Text } = Typography;

const DEFAULT_FILTERS = {
  preset: '30',
  dateRange: resolvePresetRange('30'),
  userId: null,
  period: 'day',
};

const makeDefaultFilters = () => ({
  preset: '30',
  dateRange: resolvePresetRange('30'),
  userId: null,
  period: 'day',
});

const formatDuration = (sec) => {
  if (!sec) return '0 мин';
  if (sec < 60) return `${sec} сек`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h > 0) return `${h} ч ${m} мин`;
  return `${m} мин`;
};

const formatDateTime = (v) => (v ? new Date(v).toLocaleString('ru-RU') : '—');

const AdminWorkControl = () => {
  const api = useAdminSocketContext();
  const { onlineUserIds } = api;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [requestsTimeSeries, setRequestsTimeSeries] = useState([]);
  const [usersSummary, setUsersSummary] = useState([]);
  const [dailyActivity, setDailyActivity] = useState([]);
  const [sessionsDetail, setSessionsDetail] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const sessionsChartRef = useRef(null);
  const requestsChartRef = useRef(null);
  const distributionChartRef = useRef(null);

  useEffect(() => {
    api.getFiltersMeta().then((res) => setMeta(res.meta)).catch(() => {});
  }, [api]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = filtersToParams(filters);
    try {
      const [ov, tl, series, summary, daily, sessions] = await Promise.all([
        api.getWorkOverview(params),
        api.getActivityTimeline(params),
        api.getRequestsByTypeTimeSeries(params),
        api.getUsersWorkSummary(params),
        api.getDailyActivity(params),
        api.getSessionsDetail(params),
      ]);
      setOverview(ov.overview);
      setTimeline(tl.timeline);
      setRequestsTimeSeries(series.series || []);
      setUsersSummary(summary.rows || []);
      setDailyActivity(daily.rows || []);
      setSessionsDetail(sessions.rows || []);
      setLastLoadedAt(new Date().toISOString());
    } catch (e) {
      message.error(e.message || 'Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const requestTypes = useMemo(() => meta?.request_types || [], [meta?.request_types]);
  const groupKeys = useMemo(() => groupKeysFromMeta(requestTypes), [requestTypes]);

  const tableColumns = useMemo(() => {
    const cols = [
      {
        title: 'Пользователь',
        dataIndex: 'username',
        fixed: 'left',
        width: 130,
        sorter: (a, b) => a.username.localeCompare(b.username),
      },
      {
        title: 'Запросов',
        dataIndex: 'requests_total',
        width: 90,
        sorter: (a, b) => a.requests_total - b.requests_total,
        defaultSortOrder: 'descend',
      },
      {
        title: 'В сервисе',
        dataIndex: 'session_sec',
        width: 100,
        render: formatDuration,
        sorter: (a, b) => a.session_sec - b.session_sec,
      },
      {
        title: 'Активно',
        dataIndex: 'active_sec',
        width: 90,
        render: formatDuration,
        sorter: (a, b) => a.active_sec - b.active_sec,
      },
      {
        title: 'Пассивно',
        dataIndex: 'passive_sec',
        width: 90,
        render: formatDuration,
        sorter: (a, b) => a.passive_sec - b.passive_sec,
      },
      {
        title: 'Входов',
        dataIndex: 'logins',
        width: 80,
        sorter: (a, b) => a.logins - b.logins,
      },
      {
        title: 'Первый вход',
        dataIndex: 'first_login',
        width: 140,
        render: formatDateTime,
      },
      {
        title: 'Последний вход',
        dataIndex: 'last_login',
        width: 140,
        render: formatDateTime,
      },
      {
        title: 'Ошибок',
        dataIndex: 'errors',
        width: 80,
        sorter: (a, b) => a.errors - b.errors,
        render: (v) => (v > 0 ? <Tag color="red">{v}</Tag> : v),
      },
    ];
    for (const gk of groupKeys) {
      cols.push({
        title: getRequestGroupLabel(gk),
        key: gk,
        width: 120,
        ellipsis: true,
        render: (_, r) => aggregateByTypeObject(r.by_type)[gk] || 0,
        sorter: (a, b) =>
          (aggregateByTypeObject(a.by_type)[gk] || 0) -
          (aggregateByTypeObject(b.by_type)[gk] || 0),
      });
    }
    return cols;
  }, [groupKeys]);

  const dailyColumns = [
    {
      title: 'Дата',
      dataIndex: 'date',
      width: 110,
      render: (v) => new Date(v).toLocaleDateString('ru-RU'),
    },
    { title: 'Первый вход', dataIndex: 'first_login', render: formatDateTime, width: 150 },
    { title: 'Последний вход', dataIndex: 'last_login', render: formatDateTime, width: 150 },
    { title: 'Входов', dataIndex: 'login_count', width: 80 },
    { title: 'Первое подключение', dataIndex: 'first_connect', render: formatDateTime, width: 150 },
    { title: 'Последнее отключение', dataIndex: 'last_disconnect', render: formatDateTime, width: 150 },
    { title: 'Запросов', dataIndex: 'requests_count', width: 90 },
  ];

  const handleExport = async () => {
    setExporting(true);
    try {
      const [dateFrom, dateTo] = filters.dateRange || [];
      const periodLabel = PERIOD_CHART_TITLES[filters.period] || 'По дням';
      const userLabel = filters.userId
        ? (meta?.users || []).find((u) => u.id === filters.userId)?.username
        : 'Все пользователи';

      const metaLines = [
        `Период: ${dateFrom?.format?.('DD.MM.YYYY') ?? '—'} — ${dateTo?.format?.('DD.MM.YYYY') ?? '—'}`,
        `Пользователь: ${userLabel}`,
        `Группировка запросов: ${periodLabel}`,
        `Сформировано: ${new Date().toLocaleString('ru-RU')}`,
      ];

      const { sheetCount } = await exportUsersWorkSummary(
        usersSummary,
        groupKeys,
        sessionsDetail,
        dailyActivity,
        {
          metaLines,
          chartBlocks: [
            { title: filters.userId ? 'Сессии пользователя' : 'Сессии работы (онлайн)', element: sessionsChartRef.current },
            { title: periodLabel, element: requestsChartRef.current },
            { title: 'Структура нагрузки', element: distributionChartRef.current },
          ],
        }
      );

      message.success(`Отчёт выгружен (${sheetCount} листов, включая графики)`);
    } catch (e) {
      message.error(e.message || 'Не удалось выгрузить отчёт');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="admin-tab-toolbar">
        <AdminOnlineIndicator onlineUserIds={onlineUserIds} users={meta?.users} />
        <Button
          type="primary"
          icon={<Download size={16} />}
          onClick={handleExport}
          loading={exporting}
          disabled={!usersSummary.length || loading}
        >
          Выгрузить в Excel
        </Button>
      </div>

      <AdminDateFilters
        filters={filters}
        onChange={setFilters}
        onApply={load}
        onReset={() => setFilters(makeDefaultFilters())}
        loading={loading}
        meta={meta}
      />

      <div className="admin-list-meta">
        {lastLoadedAt && (
          <span className="admin-list-meta__muted">
            Обновлено: {formatShortDateTime(lastLoadedAt)}
          </span>
        )}
      </div>

      <Spin spinning={loading}>
        <p className="admin-section-label">Сводка за период</p>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={6} lg={4}>
            <AdminKpiCard
              title="Входов"
              value={overview?.logins ?? 0}
              icon={LogIn}
              variant="blue"
              hint="Сколько раз пользователи входили в сервис за период"
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <AdminKpiCard
              title="Подключений"
              value={overview?.sessions_count ?? 0}
              icon={Activity}
              variant="purple"
              hint="Количество сессий сокета за период"
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <AdminKpiCard
              title="Время в сервисе"
              value={formatDuration(overview?.session_sec)}
              icon={Clock}
              variant="cyan"
              hint="Суммарная длительность сессий пользователей"
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <AdminKpiCard
              title="Активное время"
              value={formatDuration(overview?.active_sec)}
              icon={MousePointer}
              variant="green"
              hint="Время, когда пользователь совершал действия"
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <AdminKpiCard
              title="Пассивное время"
              value={formatDuration(overview?.passive_sec)}
              icon={Coffee}
              variant="orange"
              hint="Время в сервисе без активных действий"
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <AdminKpiCard
              title="Запросов"
              value={overview?.requests_total ?? 0}
              icon={Zap}
              variant="green"
              hint="Поисковые подзапросы за период; красный бейдж — ошибки"
              suffix={
                overview?.errors > 0 ? (
                  <Tag color="red" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                    {overview.errors}
                  </Tag>
                ) : null
              }
            />
          </Col>
        </Row>

        {(overview?.first_login || overview?.last_login) && (
          <Card size="small" className="admin-chart-card admin-activity-hints">
            <Text type="secondary">
              Первый вход: <Text strong>{formatDateTime(overview.first_login)}</Text>
              {' · '}
              Последний вход: <Text strong>{formatDateTime(overview.last_login)}</Text>
              {overview.first_request && (
                <>
                  {' · '}
                  Первый запрос: <Text strong>{formatDateTime(overview.first_request)}</Text>
                  {' · '}
                  Последний запрос: <Text strong>{formatDateTime(overview.last_request)}</Text>
                </>
              )}
            </Text>
          </Card>
        )}

        <p className="admin-section-label">Графики</p>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card
              title={filters.userId ? 'Сессии пользователя' : 'Сессии работы (онлайн)'}
              className="admin-chart-card"
            >
              {loading && !sessionsDetail.length ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : (
                <div ref={sessionsChartRef} data-export-clone-root className="admin-export-capture">
                  <WorkSessionsTimeline
                    sessions={sessionsDetail}
                    userId={filters.userId}
                    username={
                      filters.userId
                        ? (meta?.users || []).find((u) => u.id === filters.userId)?.username
                        : null
                    }
                    dateFrom={timeline?.dateFrom || filters.dateRange?.[0]?.toISOString?.()}
                    dateTo={timeline?.dateTo || filters.dateRange?.[1]?.toISOString?.()}
                    loading={loading}
                  />
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title={PERIOD_CHART_TITLES[filters.period] || PERIOD_CHART_TITLES.day}
              className="admin-chart-card"
            >
              {loading && !requestsTimeSeries.length ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : (
                <div ref={requestsChartRef} data-export-clone-root className="admin-export-capture">
                  <WorkRequestsTimeline
                    series={requestsTimeSeries}
                    period={filters.period || 'day'}
                    dateFrom={timeline?.dateFrom || filters.dateRange?.[0]?.toISOString?.()}
                    dateTo={timeline?.dateTo || filters.dateRange?.[1]?.toISOString?.()}
                    loading={loading}
                  />
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <p className="admin-section-label">Структура нагрузки</p>
        <Card className="admin-chart-card">
          <div ref={distributionChartRef} data-export-clone-root className="admin-export-capture">
            <WorkDistributionCharts
              series={requestsTimeSeries}
              usersSummary={usersSummary}
              overview={overview}
              userId={filters.userId}
              loading={loading}
            />
          </div>
        </Card>

        <Card title="Активность по дням" className="admin-chart-card admin-table-card">
          <Table
            size="small"
            rowKey="date"
            dataSource={dailyActivity}
            columns={dailyColumns}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showTotal: (count) => `Дней: ${count}`,
            }}
          />
        </Card>

        <Card title="Сводка по пользователям" className="admin-chart-card admin-table-card">
          <Table
            size="small"
            rowKey="id"
            dataSource={usersSummary}
            columns={tableColumns}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (count) => `Пользователей: ${count}`,
            }}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default AdminWorkControl;
